import React, { useMemo, useRef, useState, useEffect } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatValue, getLineColor } from './chartUtils';
import { parseThresholds, matchThreshold } from './thresholdUtils';
import WidgetState from './WidgetState';

const GaugeChart = ({ data, config = {} }) => {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const processed = useMemo(() => {
    if (!data) return null;

    let value = 0;
    let metricName = '';

    if (data.type === 'timeseries' && data.data) {
      const series = data.data[0];
      if (series && series.values && series.values.length > 0) {
        const lastPoint = series.values[series.values.length - 1];
        value = parseFloat(lastPoint[1]) || 0;
        if (series.metric && series.metric.__name__) {
          metricName = series.metric.__name__;
        }
      } else {
        // Empty series array ([] is truthy!) — an offline host / empty window
        // used to render a fabricated gauge pinned at 0. Show "No data" instead.
        return null;
      }
    } else if (data.type === 'scalar') {
      value = parseFloat(data.data);
      if (!Number.isFinite(value)) return null; // null scalar is not a zero
    } else if (data.type === 'table' && data.rows && data.rows.length > 0) {
      const firstRow = data.rows[0];
      const numericValue = Object.values(firstRow).find(v => typeof v === 'number');
      if (numericValue !== undefined) {
        value = numericValue;
      } else {
        return null;
      }
    } else {
      return null;
    }

    const gaugeMax = (config.yAxisMax != null && config.yAxisMax !== '') ? Number(config.yAxisMax) || 100 : 100;
    const percentage = Math.min(100, Math.max(0, (value / gaugeMax) * 100));

    // Colour from the configured thresholds evaluated against the RAW value —
    // not a hardcoded 30/70 percent band. With no thresholds configured, fall
    // back to a neutral accent from the colour scheme (not a good/bad colour).
    const thresholds = parseThresholds(config.thresholdsJson);
    const match = matchThreshold(value, thresholds);
    const color = match ? match.color : getLineColor(0, config.colorScheme);

    const gaugeData = [
      { name: 'value', value: percentage },
      { name: 'empty', value: 100 - percentage },
    ];

    return { value, metricName, percentage, color, gaugeData };
  }, [data, config.yAxisMax, config.thresholdsJson, config.colorScheme]);

  if (!processed) {
    return (
      <WidgetState isEmpty />
    );
  }

  const { value, metricName, color, gaugeData } = processed;

  // Format the display value — default to '%' if no unit configured
  const gaugeConfig = { ...config, unit: config.unit || '%' };
  const displayValue = formatValue(value, gaugeConfig);

  // Scale font sizes proportionally to the smaller container dimension AND
  // to the actual rendered value width — long values like "1.34 GiB" or
  // "12.4 ms" used to overflow the gauge arc when minDim was small. The
  // arc's inner radius is innerRadius=60% of (minDim/2)*0.9 ≈ minDim*0.27,
  // so we cap font size so the text width (~chars * 0.6 * fontSize) stays
  // within the inner diameter (~minDim * 0.54).
  const minDim = Math.min(dims.w, dims.h) || 100;
  const innerDiameter = minDim * 0.54;
  const charCount = Math.max(1, displayValue.length);
  // Approx 0.6 * fontSize per character is a safe upper bound for proportional fonts
  const widthCappedFont = Math.floor(innerDiameter / (charCount * 0.6));
  const valueFontSize = Math.max(10, Math.min(minDim * 0.18, widthCappedFont));
  const labelFontSize = Math.max(8, Math.min(minDim * 0.09, valueFontSize * 0.55));

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={gaugeData}
            cx="50%"
            cy="55%"
            startAngle={180}
            endAngle={0}
            innerRadius="60%"
            outerRadius="90%"
            paddingAngle={0}
            dataKey="value"
            cornerRadius={gaugeData[0].value > 0 ? 5 : 0}
            isAnimationActive={false}
          >
            <Cell fill={color} />
            <Cell fill="#e6e6e6" />
          </Pie>
          <text
            x="50%"
            y="52%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: `${valueFontSize}px`, fontWeight: 'bold' }}
          >
            {displayValue}
          </text>
          {metricName && (
            <text
              x="50%"
              y="68%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: `${labelFontSize}px`, fill: '#666' }}
            >
              {metricName}
            </text>
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default React.memo(GaugeChart, (prevProps, nextProps) => {
  return prevProps.data === nextProps.data &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.yAxisMax === nextProps.config?.yAxisMax &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.thresholdsJson === nextProps.config?.thresholdsJson;
});
